import React from 'react';
import { Box } from '@mui/material';
import {
  Smartphone,
  Checkroom,
  ShoppingBag,
  Diamond,
  Devices,
  Category,
  Laptop,
  Pets,
  Home,
  Chair,
  Bed,
  Kitchen,
  LocalLaundryService,
  Yard,
  Pool,
  Balcony,
  Garage,
  DirectionsCar,
  TwoWheeler,
  PedalBike,
  Train,
  Flight,
  DirectionsBoat,
  LocalGasStation,
  SportsSoccer,
  SportsBasketball,
  SportsTennis,
  SportsGolf,
  SportsVolleyball,
  SportsHockey,
  SportsBaseball,
  FitnessCenter,
  Hiking,
  Surfing,
  Build,
  Handyman,
  Construction,
  Plumbing,
  ElectricalServices,
  CleaningServices,
  Security,
  Lock,
  Key,
  LocalHospital,
  Medication,
  HealthAndSafety,
  Spa,
  LocalPharmacy,
  School,
  Work,
  Business,
  Science,
  Engineering,
  Restaurant,
  LocalPizza,
  LocalCafe,
  WineBar,
  Cake,
  Icecream,
  AccountBalanceWallet as WalletIcon,
  CreditCard,
  AttachMoney,
  Savings,
  TrendingUp,
  Description,
  Article,
  Folder,
  Archive,
  PictureAsPdf,
  Help,
  Info,
  Star,
  Favorite,
  Email,
  Phone,
  LocationOn,
  Event,
  Settings,
} from '@mui/icons-material';
import { SvgIconProps } from '@mui/material';
import { config } from '../config/environment';

export const iconMapping: Record<string, React.ComponentType<SvgIconProps>> = {
  smartphone: Smartphone,
  laptop: Laptop,
  computer: Laptop,
  tablet: Devices,
  headphones: Devices,
  speaker: Devices,
  camera: Devices,
  watch: Devices,
  tv: Devices,
  router: Devices,
  memory: Devices,
  storage: Devices,
  devices: Devices,
  clothes: Checkroom,
  'shopping-bag': ShoppingBag,
  diamond: Diamond,
  'watch-clothes': Devices,
  home: Home,
  chair: Chair,
  bed: Bed,
  kitchen: Kitchen,
  laundry: LocalLaundryService,
  yard: Yard,
  pool: Pool,
  balcony: Balcony,
  garage: Garage,
  car: DirectionsCar,
  motorcycle: TwoWheeler,
  bike: PedalBike,
  train: Train,
  flight: Flight,
  boat: DirectionsBoat,
  'gas-station': LocalGasStation,
  soccer: SportsSoccer,
  basketball: SportsBasketball,
  tennis: SportsTennis,
  golf: SportsGolf,
  volleyball: SportsVolleyball,
  hockey: SportsHockey,
  baseball: SportsBaseball,
  fitness: FitnessCenter,
  hiking: Hiking,
  surfing: Surfing,
  pets: Pets,
  build: Build,
  handyman: Handyman,
  construction: Construction,
  plumbing: Plumbing,
  electrical: ElectricalServices,
  cleaning: CleaningServices,
  security: Security,
  lock: Lock,
  key: Key,
  hospital: LocalHospital,
  medication: Medication,
  health: HealthAndSafety,
  spa: Spa,
  pharmacy: LocalPharmacy,
  school: School,
  work: Work,
  business: Business,
  book: Business,
  science: Science,
  engineering: Engineering,
  restaurant: Restaurant,
  pizza: LocalPizza,
  cafe: LocalCafe,
  wine: WineBar,
  cake: Cake,
  icecream: Icecream,
  wallet: WalletIcon,
  'credit-card': CreditCard,
  money: AttachMoney,
  savings: Savings,
  'trending-up': TrendingUp,
  document: Description,
  article: Article,
  folder: Folder,
  archive: Archive,
  pdf: PictureAsPdf,
  category: Category,
  help: Help,
  info: Info,
  star: Star,
  heart: Favorite,
  email: Email,
  phone: Phone,
  location: LocationOn,
  event: Event,
  settings: Settings,
};

export const getCategoryMaterialIcon = (icon?: string): React.ComponentType<SvgIconProps> => {
  const normalized = icon?.toLowerCase().replace(/[\s-_]/g, '') || 'category';
  return iconMapping[normalized] || Category;
};

function getApiOrigin(): string {
  return config.apiUrl.replace(/\/api$/, '');
}

function resolvePictoSrc(
  customIconUrl?: string | null,
  customIconFilename?: string | null,
  icon?: string | null
): string | null {
  if (customIconUrl) {
    if (customIconUrl.startsWith('http')) return customIconUrl;
    return `${getApiOrigin()}${customIconUrl.startsWith('/') ? '' : '/'}${customIconUrl}`;
  }
  if (customIconFilename) {
    return `${getApiOrigin()}/uploads/pictos/${customIconFilename}`;
  }
  if (icon?.startsWith('custom_')) {
    return `${getApiOrigin()}/uploads/pictos/${icon}`;
  }
  return null;
}

export interface CategoryIconProps {
  icon?: string | null;
  customIconUrl?: string | null;
  customIconFilename?: string | null;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  style?: React.CSSProperties;
  className?: string;
}

const CategoryIcon: React.FC<CategoryIconProps> = ({
  icon = 'category',
  size = 'medium',
  color,
  style,
  className,
  customIconUrl,
  customIconFilename,
}) => {
  const getSizeValue = () => {
    switch (size) {
      case 'small':
        return 20;
      case 'large':
        return 48;
      default:
        return 32;
    }
  };

  const pictoSrc = resolvePictoSrc(customIconUrl, customIconFilename, icon);
  if (pictoSrc) {
    return (
      <Box
        className={className}
        style={{
          width: getSizeValue(),
          height: getSizeValue(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color || 'inherit',
          ...style,
        }}
      >
        <img
          src={pictoSrc}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </Box>
    );
  }

  const normalizedIcon = icon?.toLowerCase().replace(/[\s-_]/g, '') || 'category';
  const MaterialIconComponent = getCategoryMaterialIcon(normalizedIcon);
  const finalColor = color || 'inherit';

  return (
    <MaterialIconComponent
      className={className}
      style={{
        fontSize: getSizeValue(),
        color: finalColor,
        ...style,
      }}
    />
  );
};

export default CategoryIcon;
